/**
 * AdminAnnouncements — Envoi de notifications (Message admin / Annonce globale)
 * Utilise: /api/v1/admin/notifications (POST) + /api/v1/admin/moderation/queue (search user)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Megaphone, User as UserIcon, Send, Loader2, Users, Search } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { api, formatApiError } from "../services/api";

const ROLE_OPTIONS = [
  { v: "",             label: "Tous les rôles" },
  { v: "talent",       label: "Talents" },
  { v: "employeur",    label: "Employeurs" },
  { v: "freelance",    label: "Freelances" },
  { v: "universite",   label: "Universités" },
  { v: "entrepreneur", label: "Entrepreneurs" },
  { v: "investisseur", label: "Investisseurs" },
  { v: "coach",        label: "Coachs" },
];

export default function AdminAnnouncements() {
  const [audience, setAudience] = useState("all");   // "all" | "user"
  const [title, setTitle]       = useState("");
  const [body, setBody]         = useState("");
  const [role, setRole]         = useState("");
  const [users, setUsers]       = useState([]);
  const [query, setQuery]       = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [sending, setSending]   = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/v1/admin/moderation/queue?limit=500");
      setUsers(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (audience === "user") loadUsers();
  }, [audience, loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 40);
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    ).slice(0, 40);
  }, [users, query]);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Titre et contenu obligatoires.");
      return;
    }
    if (audience === "user" && !selectedUser) {
      toast.error("Sélectionnez un destinataire.");
      return;
    }
    setSending(true);
    try {
      const body_req = audience === "all"
        ? { all: true, role: role || null, title, body, icon: "megaphone" }
        : { recipient_id: selectedUser.id, title, body, icon: "megaphone" };
      const { data } = await api.post("/v1/admin/notifications", body_req);
      toast.success(
        audience === "all"
          ? `Annonce envoyée à ${data.sent} membre(s).`
          : `Message envoyé à ${selectedUser.name}.`
      );
      setTitle("");
      setBody("");
      setSelectedUser(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <PrivateLayout
      title="Notifications & Annonces"
      breadcrumbs={[{ label: "Administration" }, { label: "Communication" }]}
    >
      <div className="afr-card" data-testid="ann-header" style={{ padding: "1.2rem 1.4rem", marginBottom: "1.2rem", background: "linear-gradient(135deg, color-mix(in oklab, var(--afr-primary) 8%, var(--afr-surface)), var(--afr-surface))", borderColor: "color-mix(in oklab, var(--afr-primary) 25%, var(--afr-border))" }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--afr-primary)", color: "#fff", display: "grid", placeItems: "center" }}>
            <Megaphone size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontFamily: "'Outfit',sans-serif", color: "var(--afr-secondary)" }}>
              Envoi manuel de notifications
            </h3>
            <p className="afr-muted" style={{ margin: "0.3rem 0 0", fontSize: "0.9rem" }}>
              Diffusez un message à un utilisateur précis ou à l'ensemble de la communauté AFRICTAL. La notification apparaît dans la cloche de chaque destinataire.
            </p>
          </div>
        </div>
      </div>

      {/* Audience switcher */}
      <div className="afr-card" style={{ padding: "1rem 1.2rem", marginBottom: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }} data-testid="ann-audience">
        <button type="button" onClick={() => setAudience("all")} className={`afr-btn afr-btn--sm ${audience === "all" ? "afr-btn--primary" : "afr-btn--ghost"}`} data-testid="ann-audience-all">
          <Users size={14} /> Annonce Globale
        </button>
        <button type="button" onClick={() => setAudience("user")} className={`afr-btn afr-btn--sm ${audience === "user" ? "afr-btn--primary" : "afr-btn--ghost"}`} data-testid="ann-audience-user">
          <UserIcon size={14} /> Message à un utilisateur
        </button>
      </div>

      {audience === "all" && (
        <div className="afr-card" style={{ padding: "1rem 1.2rem", marginBottom: "1rem" }} data-testid="ann-role-select">
          <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
            Cible (optionnel — laissez « tous » pour diffuser à toute la communauté)
          </label>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="ann-role"
            style={{ padding: "0.55rem 0.7rem", borderRadius: 8, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", color: "var(--afr-secondary)", fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", minWidth: 220 }}
          >
            {ROLE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
      )}

      {audience === "user" && (
        <div className="afr-card" style={{ padding: "1rem 1.2rem", marginBottom: "1rem" }} data-testid="ann-user-picker">
          <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>
            Destinataire {selectedUser && <span style={{ color: "var(--afr-primary)" }}>· {selectedUser.name} ({selectedUser.email})</span>}
          </label>
          <div style={{ position: "relative", marginBottom: "0.6rem" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--afr-muted)" }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom ou email…"
              data-testid="ann-user-search"
              style={{ width: "100%", padding: "0.55rem 0.7rem 0.55rem 2rem", borderRadius: 8, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-secondary)" }}
            />
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--afr-border)", borderRadius: 10 }} data-testid="ann-user-list">
            {loadingUsers ? (
              <div style={{ padding: "1rem", textAlign: "center" }}><Loader2 size={16} className="afr-spin" /></div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: "1rem", textAlign: "center", color: "var(--afr-muted)", fontSize: "0.86rem" }}>Aucun utilisateur ne correspond.</div>
            ) : (
              filteredUsers.map((u) => {
                const active = selectedUser?.id === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setSelectedUser(u)}
                    data-testid={`ann-user-${u.id}`}
                    style={{
                      display: "flex", width: "100%", padding: "0.6rem 0.8rem", border: "none",
                      background: active ? "color-mix(in oklab, var(--afr-primary) 10%, transparent)" : "transparent",
                      cursor: "pointer", textAlign: "left", alignItems: "center", gap: "0.6rem",
                      borderBottom: "1px solid var(--afr-border)",
                    }}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--afr-primary)", color: "#fff", display: "grid", placeItems: "center", fontSize: "0.78rem", fontWeight: 700 }}>
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontFamily: "'Outfit',sans-serif", fontSize: "0.9rem", color: "var(--afr-secondary)" }}>{u.name}</strong>
                      <div className="afr-muted" style={{ fontSize: "0.78rem" }}>{u.email} · {u.role}</div>
                    </div>
                    {active && <span style={{ fontSize: "0.72rem", color: "var(--afr-primary)", fontWeight: 700 }}>Sélectionné</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Message form */}
      <div className="afr-card" style={{ padding: "1.2rem 1.4rem" }} data-testid="ann-form">
        <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>Titre</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex. Nouvelle fonctionnalité disponible"
          maxLength={140}
          data-testid="ann-title"
          style={{ width: "100%", padding: "0.6rem 0.8rem", borderRadius: 10, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'Outfit',sans-serif", fontSize: "0.95rem", color: "var(--afr-secondary)", marginBottom: "0.8rem" }}
        />

        <label className="afr-muted" style={{ fontSize: "0.78rem", display: "block", marginBottom: "0.3rem" }}>Contenu</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Rédigez votre message. Il apparaîtra dans la cloche 🔔 des destinataires."
          rows={6}
          maxLength={2000}
          data-testid="ann-body"
          style={{ width: "100%", padding: "0.7rem 0.8rem", borderRadius: 10, border: "1px solid var(--afr-border)", background: "var(--afr-surface)", fontFamily: "'Outfit',sans-serif", fontSize: "0.94rem", color: "var(--afr-secondary)", resize: "vertical" }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem", flexWrap: "wrap", gap: "0.6rem" }}>
          <span className="afr-muted" style={{ fontSize: "0.82rem", display: "inline-flex", gap: "0.4rem", alignItems: "center" }}>
            <Megaphone size={14} style={{ color: "var(--afr-primary)" }} />
            {audience === "all"
              ? role
                ? `Cible : ${ROLE_OPTIONS.find((r) => r.v === role)?.label}`
                : "Diffusion à tous les membres AFRICTAL"
              : selectedUser
                ? `Destinataire : ${selectedUser.name}`
                : "Sélectionnez un destinataire"}
          </span>
          <button type="button" onClick={send} disabled={sending || !title.trim() || !body.trim() || (audience === "user" && !selectedUser)} className="afr-btn afr-btn--primary" data-testid="ann-send">
            {sending ? <Loader2 size={14} className="afr-spin" /> : <Send size={14} />}
            {audience === "all" ? "Envoyer l'annonce" : "Envoyer le message"}
          </button>
        </div>
      </div>
    </PrivateLayout>
  );
}
